import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { ProjectsService } from '../../../core/services/projects.service';
import { Project } from '../../../core/models/project.model';
import { CurrencyService } from '../../../core/services/currency.service';
import { LoadingSpinnerComponent } from '../../../shared/components/loading-spinner/loading-spinner.component';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NotificationService } from '../../../services/notification.service';
import { CountrySelectorComponent } from '../../../shared/components/country-selector/country-selector.component';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { PropertyUploadService } from '../../../core/services/property-upload.service';
import { Store } from '@ngrx/store';
import { selectCurrentUser, selectActor } from '../../../store/auth/auth.selectors';
import { permissionSetFromRaw } from '../../../shared/utils/permissions.util';

@Component({
  selector: 'app-developer-projects',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    LoadingSpinnerComponent,
    LucideIconsModule,
    ReactiveFormsModule,
    CountrySelectorComponent,
  ],
  templateUrl: './developer-projects.component.html',
  styleUrls: ['./developer-projects.component.scss'],
})
export class DeveloperProjectsComponent implements OnInit, OnDestroy {
  private projectsService = inject(ProjectsService);
  private fb = inject(FormBuilder);
  private notifications = inject(NotificationService);
  private uploadService = inject(PropertyUploadService);
  private currencyService = inject(CurrencyService);
  private store = inject(Store);
  private router = inject(Router);
  private destroy$ = new Subject<void>();
  private handledNewProjectRoute = false;

  projects: Project[] = [];
  loading = true;
  loadingPage = false;
  error: string | null = null;
  currentPage = 1;
  pageSize = 10;
  hasNextPage = true;
  showCreate = false;
  saving = false;
  uploadingProjectImage = false;
  createdProjectId: string | null = null;
  projectImageFailed: Record<string, boolean> = {};
  partnerLogoFailed: Record<string, boolean> = {};
  projectImageFile: File | null = null;
  projectImagePreview: string | null = null;
  imageError: string | null = null;
  brochureFile: File | null = null;
  brochureFileName: string | null = null;
  masterPlanFile: File | null = null;
  masterPlanFileName: string | null = null;
  documentsError: string | null = null;
  totalCount = 0;
  reservationsFeatureAvailable = false;
  private permissionCodes: string[] = [];
  private actorType: string | null = null;
  private actorRoleCode: string | null = null;
  createForm: FormGroup = this.fb.group({
    name: ['', Validators.required],
    description: [''],
    developmentBadgeMode: ['off_plan'],
    developmentBadgeCustom: [''],
    country: ['', Validators.required],
    locationAddress: [''],
    locationLink: [''],
    reservationsEnabled: [false],
    sharedWithOrg: [false],
  });
  ngOnInit(): void {
    // The actor slice reliably carries actor_type/role_code (derived from the JWT).
    // selectCurrentUser (/users/me) does NOT include actor_type, so read it here —
    // otherwise the "New Project" button (open to any org actor) stays disabled for
    // team members who lack projects:write.
    this.store
      .select(selectActor)
      .pipe(takeUntil(this.destroy$))
      .subscribe((actor: any) => {
        this.actorType = actor?.actor_type ?? actor?.actorType ?? actor?.type ?? null;
        this.actorRoleCode = actor?.role_code ?? actor?.roleCode ?? null;
      });
    this.store
      .select(selectCurrentUser)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: any) => {
        this.reservationsFeatureAvailable = Boolean(
          user?.feature_flags?.reservations_enabled,
        );
        const raw = user?.permission_codes ?? user?.permissionCodes ?? [];
        this.permissionCodes = Array.isArray(raw)
          ? raw.map((value: unknown) => String(value || '').trim())
          : [];
        // Fallback if the actor slice hasn't populated actor_type yet.
        if (!this.actorType) {
          this.actorType = user?.actor_type ?? user?.actorType ?? user?.type ?? null;
        }
        this.openCreateFromRouteIfNeeded();
      });
    this.reload();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  startingPriceLabel(project: Project): string | null {
    const value = project?.startingPrice;
    if (value === null || value === undefined || !Number.isFinite(value))
      return null;
    const fromCode = project?.startingCurrency || 'EGP';
    const converted = this.currencyService.convert(value, fromCode);
    const currencyCode = this.currencyService.currentCurrency();
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'code',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
        .format(converted)
        .replace(/[\u00A0\u202F]/g, ' ');
    } catch {
      const amount = new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0,
      }).format(converted);
      return `${currencyCode} ${amount}`;
    }
  }

  reload() {
    this.currentPage = 1;
    this.projects = [];
    this.projectImageFailed = {};
    this.partnerLogoFailed = {};
    this.loadProjects(false);
  }

  showMore() {
    if (!this.hasNextPage || this.loadingPage) return;
    this.currentPage += 1;
    this.loadProjects(true);
  }

  private loadProjects(append = false) {
    if (this.currentPage === 1) append = false;
    this.loading = this.currentPage === 1;
    this.error = null;
    this.loadingPage = true;
    this.projectsService
      .getProjectsAuth({ page: this.currentPage, perPage: this.pageSize })
      .subscribe({
        next: ({ items, total, page, perPage }) => {
          if (append) {
            const seen = new Set(this.projects.map((p) => p.id));
            this.projects = [
              ...this.projects,
              ...items.filter((p) => !seen.has(p.id)),
            ];
          } else {
            this.projects = items;
          }
          const incomingIds = new Set(
            (items || [])
              .map((project) => String(project?.id || '').trim())
              .filter(Boolean),
          );
          this.projectImageFailed = Object.fromEntries(
            Object.entries(this.projectImageFailed).filter(
              ([projectId]) => !incomingIds.has(projectId),
            ),
          );
          this.partnerLogoFailed = Object.fromEntries(
            Object.entries(this.partnerLogoFailed).filter(
              ([projectId]) => !incomingIds.has(projectId),
            ),
          );
          this.totalCount = total;
          const serverHasNext = total > page * perPage;
          this.hasNextPage = serverHasNext || items.length >= perPage;
          this.loading = false;
          this.loadingPage = false;
        },
        error: () => {
          this.loading = false;
          this.loadingPage = false;
          this.error = 'Failed to load projects';
          if (!append) {
            this.projects = [];
            this.totalCount = 0;
          } else if (this.currentPage > 1) {
            this.currentPage -= 1;
          }
        },
      });
  }

  canWriteProjects(): boolean {
    // Project creation is open to any org actor ("any user can create a
    // project"). Editing/deleting an existing project is gated separately on the
    // detail page (owner OR projects:write). Investors are the only actors who
    // cannot create — and they can't reach this page anyway (DeveloperActorGuard).
    if (this.actorRoleCode === 'investor' || this.actorType === 'investor') return false;
    if (this.actorType === 'org' || this.actorType === 'app_admin') return true;
    if (this.actorRoleCode) return true; // any resolved org role (sales, subpartner, partner, developer, admin…)
    // Last-resort fallback for older sessions where neither actor field populated
    // yet: keep the permission-based allowance so the button still works.
    return permissionSetFromRaw(this.permissionCodes).has('projects:write');
  }

  private openCreateFromRouteIfNeeded(): void {
    if (this.handledNewProjectRoute) return;
    const path = this.router.url.split('?')[0].replace(/\/+$/, '');
    if (!path.endsWith('/developer/projects/new')) return;
    this.handledNewProjectRoute = true;
    this.openCreate();
  }

  private showRestrictedAccess(): void {
    this.notifications
      .showError(
        'Your access is restricted. Contact your administrator to enable this action.',
      )
      .subscribe();
  }

  openCreate() {
    if (this.saving) return;
    if (!this.canWriteProjects()) {
      this.showRestrictedAccess();
      return;
    }
    this.createdProjectId = null;
    this.createForm.enable();
    this.showCreate = true;
    this.error = null;
    this.imageError = null;
    this.documentsError = null;
    this.createForm.reset({
      name: '',
      description: '',
      developmentBadgeMode: 'off_plan',
      developmentBadgeCustom: '',
      country: '',
      locationAddress: '',
      locationLink: '',
      reservationsEnabled: false,
      sharedWithOrg: false,
    });
    this.projectImageFile = null;
    this.projectImagePreview = null;
    this.brochureFile = null;
    this.brochureFileName = null;
    this.masterPlanFile = null;
    this.masterPlanFileName = null;
  }

  closeCreate() {
    if (this.saving) return;
    this.createdProjectId = null;
    this.createForm.enable();
    this.showCreate = false;
    this.projectImageFile = null;
    this.projectImagePreview = null;
    this.imageError = null;
    this.documentsError = null;
    this.brochureFile = null;
    this.brochureFileName = null;
    this.masterPlanFile = null;
    this.masterPlanFileName = null;
  }

  async submitCreate() {
    if (!this.canWriteProjects()) {
      this.showRestrictedAccess();
      return;
    }
    if (this.saving) return;
    if (this.createdProjectId) {
      if (!this.projectImageFile) return;
      await this.finishProjectImage(this.createdProjectId);
      return;
    }
    if (this.createForm.invalid || this.imageError) return;
    this.saving = true;
    this.error = null;
    this.imageError = null;
    this.documentsError = null;
    const value = this.createForm.value;
    if (!value.country) {
      this.error = 'Country is required';
      this.notifications.showError(this.error).subscribe();
      this.saving = false;
      return;
    }
    const payload: Record<string, unknown> = {
      name: value.name,
      description: value.description || null,
      status: 'Active',
    };

    const badgeMode = String(value.developmentBadgeMode || '').trim();
    const badgePresetMap: Record<string, string> = {
      funding: 'Funding',
      new_launch: 'New launch',
      development_off_plan: 'New Development/Off-Plan',
      off_plan: 'Off-plan',
      under_construction: 'Under construction',
    };
    const badgeValueRaw =
      badgeMode === 'custom'
        ? String(value.developmentBadgeCustom || '').trim()
        : badgePresetMap[badgeMode] || badgePresetMap['off_plan'];
    if (!badgeValueRaw) {
      this.error = 'Please provide a custom development badge label.';
      this.notifications.showError(this.error).subscribe();
      this.saving = false;
      return;
    }
    if (badgeValueRaw.length > 60) {
      this.error = 'Development badge must be 60 characters or less.';
      this.notifications.showError(this.error).subscribe();
      this.saving = false;
      return;
    }
    payload['development_badge'] = badgeValueRaw;
    payload['country'] =
      String(value.country || '')
        .trim()
        .toUpperCase() || null;
    payload['location_address'] =
      String(value.locationAddress || '').trim() || null;
    payload['reservations_enabled'] = this.reservationsFeatureAvailable
      ? Boolean(value.reservationsEnabled)
      : false;
    // Share the new project with the whole org (visible to all org members) or
    // keep it private to the creator until shared. The creator can always edit
    // their own project regardless of this flag.
    payload['shared_with_org'] = Boolean(value.sharedWithOrg);
    const locationLink = String(value.locationLink || '').trim() || null;
    if (locationLink && !/^https?:\/\//i.test(locationLink)) {
      this.error = 'Location link must start with http(s)://';
      this.notifications.showError(this.error).subscribe();
      this.saving = false;
      return;
    }
    payload['location_link'] = locationLink;
    try {
      if (this.brochureFile) {
        const uploaded = await firstValueFrom(
          this.uploadService.uploadFile(this.brochureFile, {
            isPublic: true,
            owner: 'org',
            context: 'project',
          }),
        );
        if (uploaded?.file_id) payload['brochure_file_id'] = uploaded.file_id;
      }
      if (this.masterPlanFile) {
        const uploaded = await firstValueFrom(
          this.uploadService.uploadFile(this.masterPlanFile, {
            isPublic: true,
            owner: 'org',
            context: 'project',
          }),
        );
        if (uploaded?.file_id)
          payload['master_plan_file_id'] = uploaded.file_id;
      }

      const created = await firstValueFrom(
        this.projectsService.createProject(payload),
      );

      this.createdProjectId = created.id;
      // Details and documents are already saved; retries must only upload the image.
      this.createForm.disable();
      await this.finishProjectImage(created.id);
    } catch (err: any) {
      this.error = this.getErrorMessage(err, 'Failed to create project');
      this.notifications.showError(this.error).subscribe();
    } finally {
      this.saving = false;
    }
  }

  private async finishProjectImage(projectId: string): Promise<void> {
    this.saving = true;
    this.imageError = null;
    const file = this.projectImageFile;
    try {
      if (file) {
        this.uploadingProjectImage = true;
        await firstValueFrom(this.projectsService.uploadProjectImage(projectId, file));
      }
      this.notifications.showSuccess('Project created').subscribe();
      this.saving = false;
      this.closeCreate();
      this.reload();
    } catch (err) {
      this.imageError = 'Project created, but the cover image was not saved. ' +
        this.getErrorMessage(err, 'Image upload failed') +
        '. Retry the image upload, or close this dialog and add it from the project details.';
      this.notifications.showWarning(this.imageError).subscribe();
      this.reload();
    } finally {
      this.uploadingProjectImage = false;
      this.saving = false;
    }
  }

  onProjectImageSelected(event: Event) {
    if (this.saving) return;
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    input.value = '';
    this.imageError = null;

    if (!file) return;
    if (!file.type.startsWith('image/') || file.size === 0 || file.size > 20 * 1024 * 1024) {
      this.projectImageFile = null;
      this.projectImagePreview = null;
      this.imageError = 'Please upload a non-empty image of 20 MB or less.';
      return;
    }
    this.projectImageFile = file;
    const reader = new FileReader();
    reader.onload = () => {
      this.projectImagePreview =
        typeof reader.result === 'string' ? reader.result : null;
    };
    reader.readAsDataURL(file);
  }

  clearProjectImageSelection() {
    if (this.saving) return;
    this.projectImageFile = null;
    this.projectImagePreview = null;
    this.imageError = null;
  }

  onBrochureSelected(event: Event) {
    if (this.saving || this.createdProjectId) return;
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    input.value = '';
    this.documentsError = null;
    if (!file) return;
    this.brochureFile = file;
    this.brochureFileName = file.name || 'Brochure';
  }

  clearBrochureSelection() {
    if (this.saving || this.createdProjectId) return;
    this.brochureFile = null;
    this.brochureFileName = null;
    this.documentsError = null;
  }

  onMasterPlanSelected(event: Event) {
    if (this.saving || this.createdProjectId) return;
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    input.value = '';
    this.documentsError = null;
    if (!file) return;
    this.masterPlanFile = file;
    this.masterPlanFileName = file.name || 'Master Plan';
  }

  clearMasterPlanSelection() {
    if (this.saving || this.createdProjectId) return;
    this.masterPlanFile = null;
    this.masterPlanFileName = null;
    this.documentsError = null;
  }

  private getErrorMessage(err: any, fallback: string): string {
    return err?.error?.error?.message || err?.error?.message || err?.message || fallback;
  }

  onProjectImageError(projectId: string | null) {
    if (!projectId) return;
    this.projectImageFailed = { ...this.projectImageFailed, [projectId]: true };
  }

  onPartnerLogoError(projectId: string | null) {
    if (!projectId) return;
    this.partnerLogoFailed = { ...this.partnerLogoFailed, [projectId]: true };
  }

  statusLabel(status?: string | null): string {
    if (!status) return 'Active';
    return status.toString().replace(/_/g, ' ') || 'Active';
  }

  /**
   * Draft/Unpublished are internal workflow states. Everyone in the org can
   * work the project either way, so flagging it only invites "why does my
   * project say Draft?" questions. Super admins (app_admin) DO need the
   * lifecycle state — they run the publishing/approval console — so the
   * badges stay visible for them.
   */
  get isSuperAdmin(): boolean {
    return this.actorRoleCode === 'app_admin' || this.actorType === 'app_admin';
  }

  /** Status pill: hidden for draft/unpublished unless super admin. */
  showProjectStatusBadge(project: Project): boolean {
    if (this.isSuperAdmin) return true;
    const key = String(project?.status || '').trim().toLowerCase();
    return key !== 'draft' && key !== 'unpublished';
  }

  developerName(project: Project): string | null {
    const name =
      project.organizationName ||
      project.vendorName ||
      project.creatorName ||
      null;
    if (!name) return null;
    const normalized = name.trim().toLowerCase();
    if (normalized.includes('nibni support')) {
      return null;
    }
    return name;
  }
}
